import { apiClient } from "../client";
import type { Envelope, LaravelPaginator } from "./catalog";

/**
 * Seeker-portal reads for the website dashboard. Every path is Sanctum-guarded
 * (`token` in the Postman collection) so it authenticates through the BFF's
 * session cookie — the same channel the rest of the app already uses.
 *
 * Upstream response shapes are only loosely documented, so the types here are
 * permissive and the consuming hook normalises defensively.
 */

/** Authenticated user profile — richer than the cached SSO session user. */
export interface DashboardUser {
  id: number | string;
  name: string;
  email: string;
  mobile?: string | null;
  /** Birth details, when the profile carries them. */
  dob?: string | null;
  date_of_birth?: string | null;
  /** As entered: 'HH:mm' from the site, 'h:mm AM/PM' from the mobile app. */
  time_of_birth?: string | null;
  place_of_birth?: string | null;
  is_birth_time_unknown?: boolean;
  /** Older aliases — read as fallbacks only. */
  birth_time?: string | null;
  birth_place?: string | null;
  /** Avatar URL, set via POST /user/profile/photo. */
  profile_image?: string | null;
  /** Karma balance (multiples of 0.5) — earned on shop orders, read-only. */
  karma_points?: number;
  [key: string]: unknown;
}

export interface Booking {
  id: number;
  /** Some payloads nest the astrologer; others flatten a name. */
  astrologer?: { name?: string; display_name?: string } | null;
  astrologer_name?: string | null;
  service?: string | null;
  title?: string | null;
  consultation_type?: string | null;
  date?: string | null;
  time?: string | null;
  scheduled_at?: string | null;
  duration?: number | string | null;
  status?: string | null;
  [key: string]: unknown;
}

/** Payload for PUT /user/profile — verified against the backend validator.
 * A FULL update despite the PUT verb: first_name, email, mobile_no and
 * country_id (integer, exists:countries,id) are required on EVERY call, so
 * resend them even when unchanged. Everything else is nullable. city/state
 * are plain name strings (max:100) here — unlike the address book, which
 * wants numeric ids. */
export interface UpdateProfilePayload {
  first_name: string;
  email: string;
  mobile_no: string;
  country_id: number;
  middle_name?: string | null;
  last_name?: string;
  alt_ph?: string;
  education?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  /** "YYYY-MM-DD"; null clears. */
  dob?: string | null;
  /** Birth details are only changed when the key is sent (omit = keep,
   * null/'' = clear). 'HH:mm' or 'h:mm AM/PM'. */
  time_of_birth?: string | null;
  /** max 255 */
  place_of_birth?: string | null;
  /** true also clears time_of_birth. */
  is_birth_time_unknown?: boolean;
  skill?: string;
  strength?: string;
  hobbies?: string;
  designation?: string;
  department?: string;
  bio?: string;
  experience_years?: number;
  alias_name?: string;
  date_of_joining?: string;
  corp_email?: string;
  alt_email?: string;
  shift?: string;
  warning?: string;
  [key: string]: unknown;
}

/** Line item on a shop order (verified against the live /orders payload). */
export interface OrderItem {
  id: number;
  order_id?: number;
  product_id?: number;
  /** Snapshot of the product name at purchase time. */
  product_name?: string | null;
  quantity?: number | string | null;
  /** Decimal strings, e.g. "2500.00". */
  price?: string | null;
  subtotal?: string | null;
  /** Full product record; carries no image fields on this endpoint. */
  product?: { id?: number; name?: string; slug?: string; [key: string]: unknown } | null;
  [key: string]: unknown;
}

/** One step of the fulfilment pipeline from GET /orders/track/:orderNumber —
 * always the four canonical stages (pending → processing → shipped →
 * delivered) with per-step reached/current flags and timestamps. */
export interface OrderTrackStep {
  status: string;
  /** The stage the order is currently sitting at. */
  active: boolean;
  /** Stages the order has already moved past. */
  completed: boolean;
  date: string | null;
}

/** Payload of GET /orders/track/:orderNumber. Unlike the /orders listing,
 * `order.items[].product` here carries image fields (image_url + media). */
export interface OrderTrackData {
  order: Order;
  status_history: OrderTrackStep[];
  /** Courier side from Shiprocket; null until the order has an AWB. */
  shipment: OrderShipment | null;
  /** Whether "Cancel order" applies: not yet with the courier (POST /orders/:orderNumber/cancel). */
  can_cancel?: boolean;
}

/** One courier scan, newest first in `OrderShipment.activities`. */
export interface ShipmentActivity {
  date: string | null;
  status: string | null;
  activity: string | null;
  location: string | null;
}

/** Live Shiprocket tracking for an order — refreshed by the API whenever the
 * stored copy is more than a few minutes old. */
export interface OrderShipment {
  awb_code: string;
  courier_name: string | null;
  /** Shiprocket's label, e.g. "IN TRANSIT", "OUT FOR DELIVERY", "DELIVERED". */
  status: string | null;
  /** Courier's public tracking page. */
  track_url: string | null;
  /** Expected delivery (ISO). */
  etd: string | null;
  pickup_scheduled_at: string | null;
  delivered_at: string | null;
  activities: ShipmentActivity[];
  /** When this was last checked with Shiprocket (ISO). */
  updated_at: string;
  // --- tracking v2 (additive) ---
  direction?: "forward" | "return";
  stage?: ShipmentStage;
  /** Courier scans, newest first. */
  events?: ShipmentEvent[];
  /** The first expected-delivery date we saw; `etd` keeps updating. */
  initial_etd?: string | null;
  is_delayed?: boolean;
  courier?: { name: string | null; awb: string | null; track_url: string | null };
  milestones?: { shipped_at: string | null; out_for_delivery_at: string | null; delivered_at: string | null };
  destination_city?: string | null;
  /** The latest unresolved failed-delivery attempt, or null. */
  ndr?: ShipmentNdr | null;
  /** The live return pickup for this order, if any (same shape). */
  return_shipment?: OrderShipment | null;
}

export type ShipmentStage =
  | "created"
  | "awb_assigned"
  | "pickup_scheduled"
  | "pickup_failed"
  | "in_transit"
  | "out_for_delivery"
  | "delivery_failed"
  | "delivered"
  | "rto"
  | "rto_delivered"
  | "cancelled"
  | "lost"
  | "unknown";

export interface ShipmentEvent {
  occurred_at: string;
  label: string | null;
  stage: ShipmentStage | null;
  activity: string | null;
  location: string | null;
}

export interface ShipmentNdr {
  id: number;
  attempt: number;
  reason: string | null;
  raised_at: string;
  /** The customer may still ask for another attempt. */
  can_respond: boolean;
  response: { action?: string; preferred_date?: string | null; responded_at?: string; submitted: boolean } | null;
}

/** Body of a failed-delivery answer (re-attempt is the only customer action). */
export interface NdrResponseInput {
  action?: "reattempt";
  /** YYYY-MM-DD, today to 3 days ahead. */
  preferred_date?: string;
  phone?: string;
  address_note?: string;
  comment?: string;
}

/** The customer dashboard's order tabs (GET /orders?status=). */
export type OrderGroup = "active" | "delivered" | "cancelled" | "returned";
export type OrderCounts = Record<OrderGroup | "all", number>;

/** Shop order as returned by GET /orders (verified against production). */
export interface Order {
  id: number;
  order_number: string;
  /** e.g. "pending" — the fulfilment status. */
  order_status?: string | null;
  /** e.g. "pending" — the payment status (separate from order_status). */
  payment_status?: string | null;
  payment_method?: string | null;
  coupon_code?: string | null;
  /** Decimal strings, e.g. "4600.00". */
  coupon_discount?: string | null;
  price_subtotal?: string | null;
  price_gst?: string | null;
  price_shipping?: string | null;
  discounted_price?: string | null;
  total_amount?: string | null;
  created_at?: string | null;
  items?: OrderItem[];
  [key: string]: unknown;
}

export const dashboard = {
  /** Authenticated user profile (GET /user). */
  me: () => apiClient.get<Envelope<DashboardUser> | DashboardUser>("/user"),

  /** Update the signed-in user's profile (PUT /user/profile). See
   * UpdateProfilePayload — a full required-field update, not a patch. */
  updateProfile: (payload: UpdateProfilePayload) =>
    apiClient.put<Envelope<DashboardUser> | DashboardUser>("/user/profile", payload),

  /** Set the signed-in user's avatar (multipart `photo`: jpg/png/webp, max
   * 5 MB). Replaces any previous photo; the response is the updated user.
   * The Content-Type override stops axios JSON-encoding the FormData and lets
   * the browser attach the multipart boundary itself. */
  uploadProfilePhoto: (file: File | Blob) => {
    const body = new FormData();
    body.append("photo", file);
    return apiClient.post<Envelope<DashboardUser> | DashboardUser>("/user/profile/photo", body, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  /** Remove the signed-in user's avatar. */
  removeProfilePhoto: () =>
    apiClient.delete<Envelope<DashboardUser> | DashboardUser>("/user/profile/photo"),

  /** The signed-in user's bookings / consultations. */
  bookings: (params: { page?: number; status?: string } = {}) =>
    apiClient.get<Envelope<LaravelPaginator<Booking> | Booking[]> | Booking[]>("/bookings", {
      params: { ...params },
    }),

  /** Sessions booked WITH an astrologer (GET /astrologer/:id/bookings) — the
   * astrologer-side counterpart of `bookings`, for astrologer accounts. */
  astrologerBookings: (astrologerId: number | string, params: { page?: number; status?: string } = {}) =>
    apiClient.get<Envelope<LaravelPaginator<Booking> | Booking[]> | Booking[]>(
      `/astrologer/${astrologerId}/bookings`,
      { params: { ...params } }
    ),

  /** The user's shop orders — a Laravel paginator in a status envelope, plus
   * `counts` per dashboard tab. `status` narrows the list to one tab's group. */
  orders: (params: { page?: number; status?: OrderGroup } = {}) =>
    apiClient.get<Envelope<LaravelPaginator<Order>> & { counts?: OrderCounts }>("/orders", { params: { ...params } }),

  /** Track one order by its public order number — full order detail (items
   * with product images, payment facts, money breakdown) plus the four-stage
   * fulfilment pipeline. */
  trackOrder: (orderNumber: string) =>
    apiClient.get<Envelope<OrderTrackData>>(`/orders/track/${encodeURIComponent(orderNumber)}`),

  /** Answer a failed delivery on the signed-in customer's order — asks the courier to try again. */
  ndrResponse: (orderNumber: string, body: NdrResponseInput) =>
    apiClient.post<Envelope<{ submitted: boolean }>>(`/orders/${encodeURIComponent(orderNumber)}/ndr-response`, body),

  /** Cancel the signed-in customer's order — allowed only before it reaches
   * Shiprocket; a paid order is refunded to the original payment via Razorpay. */
  cancelOrder: (orderNumber: string, reason: string) =>
    apiClient.post<Envelope<{ order: Order; refund: unknown | null }>>(`/orders/${encodeURIComponent(orderNumber)}/cancel`, { reason }),

  /** GET /orders/{id}/invoice streams a PDF (Shiprocket's GST invoice once the
   * order is in Shiprocket) — build the href for a link; 404 when cancelled or
   * an online payment never completed. */
  orderInvoicePath: (idOrNumber: number | string) => `/orders/${encodeURIComponent(String(idOrNumber))}/invoice`,

  /** The user's support tickets. */
  supportTickets: (params: { page?: number } = {}) =>
    apiClient.get<unknown>("/support-tickets", { params: { ...params } }),

  /** The user's course enrollments. */
  enrollments: () => apiClient.get<unknown>("/enrollments"),
};
